import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Grid,
    Paper,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Card,
    CardContent,
    CircularProgress
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { tenantAPI } from '../../services/api';

// Define the tenant type
interface Tenant {
    id: string;
    name: string;
    subdomain: string;
    userCount: number;
    status: string;
    createdAt: string;
    updatedAt: string;
}

const SuperAdminDashboard: React.FC = () => {
    const { user } = useAuth();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchTenants = async () => {
            try {
                const response = await tenantAPI.getAllTenants();
                setTenants(response.data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching tenants:', err);
                setError('Failed to load tenant data');
                setLoading(false);
            }
        };

        fetchTenants();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={() => window.location.reload()}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Container>
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" gutterBottom component="div">
                    Super Admin Dashboard
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                    Platform Administration
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Overview Stats */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                        <Typography variant="h4" color="primary">{tenants.length}</Typography>
                        <Typography variant="body1">Total Clubs</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                        <Typography variant="h4" color="primary">
                            {tenants.reduce((total, tenant) => total + tenant.userCount, 0)}
                        </Typography>
                        <Typography variant="body1">Total Users</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                        <Typography variant="h4" color="primary">
                            {tenants.filter(tenant => tenant.status === 'active').length}
                        </Typography>
                        <Typography variant="body1">Active Clubs</Typography>
                    </Paper>
                </Grid>

                {/* Tenant List */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 2, overflow: 'hidden' }}>
                        <Typography variant="h6" gutterBottom component="div">
                            Club List
                        </Typography>
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Name</strong></TableCell>
                                        <TableCell><strong>Subdomain</strong></TableCell>
                                        <TableCell align="right"><strong>Users</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Created</strong></TableCell>
                                        <TableCell><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tenants.map((tenant) => (
                                        <TableRow key={tenant.id}>
                                            <TableCell>{tenant.name}</TableCell>
                                            <TableCell>{tenant.subdomain}</TableCell>
                                            <TableCell align="right">{tenant.userCount}</TableCell>
                                            <TableCell>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        px: 1,
                                                        py: 0.5,
                                                        borderRadius: 1,
                                                        bgcolor: tenant.status === 'active' ? 'success.light' : 'warning.light',
                                                        color: tenant.status === 'active' ? 'success.dark' : 'warning.dark',
                                                    }}
                                                >
                                                    {tenant.status.toUpperCase()}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{new Date(tenant.createdAt).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"                                                    onClick={() => {
                                                        // Store tenant ID in local storage and navigate to dashboard
                                                        localStorage.setItem('tenantId', tenant.id);
                                                        // Use navigate instead of reload to avoid losing the auth context
                                                        window.location.href = '/dashboard';
                                                    }}
                                                >
                                                    Manage
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>        {/* New Tenant Card */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" gutterBottom>
                                Create New Club
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    // Navigate to tenant creation page (to be implemented)
                                    alert('Tenant creation feature will be implemented soon!');
                                }}
                            >
                                Add New Club
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default SuperAdminDashboard;
